import { useEffect, useState } from "react";
import { X, Save, Loader2 } from "lucide-react";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

// Same curated list used on the client portal so admin edits use identical keys.
const CATEGORIES = [
  "office_supplies", "meals_entertainment", "fuel_auto", "travel_lodging",
  "utilities", "software_subscriptions", "rent_lease", "insurance",
  "marketing_advertising", "professional_services", "bank_card_fees",
  "cogs", "other",
];
const DOC_TYPES = ["receipt", "bank_statement", "invoice", "check", "other"];

export default function DocumentEditModal({ doc, onClose, onSaved }) {
  const t = useT();
  const [form, setForm] = useState({
    account_label: doc.account_label || "",
    period: doc.period || "",
    doc_type: doc.doc_type || "",
    vendor: doc.vendor || "",
    amount: doc.amount ?? "",
    category_key: doc.category_key || "",
    category_other: doc.category_other || "",
    subcategory: doc.subcategory || "",
    notes: doc.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Close on Escape
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      // Convert amount to number or null
      if (payload.amount === "" || payload.amount == null) delete payload.amount;
      else payload.amount = parseFloat(payload.amount);
      const { data } = await api.patch(`/documents/${doc.id}`, payload);
      toast.success(t("admin.doc_edit.saved") || "Metadata updated");
      onSaved?.(data);
      onClose();
    } catch {
      toast.error(t("admin.doc_edit.failed") || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9998] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="admin-doc-edit-modal"
    >
      <div
        className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-[#E5E5E5] flex items-start justify-between">
          <div>
            <div className="rn3-eyebrow text-[#8B1C22]">{t("admin.doc_edit.heading") || "Amend Document"}</div>
            <div className="font-medium text-[#0A0A0A] mt-1 break-all">{doc.filename}</div>
            <div className="text-xs text-[#737373] mt-1">{doc.user_name || doc.user_email}</div>
          </div>
          <button onClick={onClose} className="text-[#737373] hover:text-[#0A0A0A]" aria-label="Close" data-testid="admin-doc-edit-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium block mb-1">{t("portal.upload.account_label") || "Account"}</label>
              <input type="text" value={form.account_label} onChange={(e) => setField("account_label", e.target.value)} className="rn3-input w-full text-sm" data-testid="admin-doc-edit-account" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">{t("portal.upload.period_label") || "Period"}</label>
              <input type="text" value={form.period} onChange={(e) => setField("period", e.target.value)} placeholder="YYYY-MM" className="rn3-input w-full text-sm" data-testid="admin-doc-edit-period" />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium block mb-1">{t("portal.upload.type_label") || "Type"}</label>
            <div className="grid grid-cols-5 gap-1">
              {DOC_TYPES.map((dt) => (
                <button
                  type="button"
                  key={dt}
                  onClick={() => setField("doc_type", dt)}
                  className={`text-[10px] uppercase tracking-widest py-1.5 border ${form.doc_type === dt ? "bg-[#8B1C22] text-white border-[#8B1C22]" : "bg-white text-[#0A0A0A] border-[#E5E5E5] hover:border-[#8B1C22]"}`}
                >
                  {t(`portal.upload.type.${dt}`) || dt.replace("_", " ")}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium block mb-1">{t("portal.upload.vendor_label") || "Vendor"}</label>
              <input type="text" value={form.vendor} onChange={(e) => setField("vendor", e.target.value)} className="rn3-input w-full text-sm" data-testid="admin-doc-edit-vendor" />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">{t("portal.upload.amount_label") || "Amount ($)"}</label>
              <input type="number" step="0.01" value={form.amount} onChange={(e) => setField("amount", e.target.value)} className="rn3-input w-full text-sm" data-testid="admin-doc-edit-amount" />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium block mb-1">{t("portal.upload.category_label") || "Category"}</label>
            <select value={form.category_key} onChange={(e) => setField("category_key", e.target.value)} className="rn3-input w-full text-sm" data-testid="admin-doc-edit-category">
              <option value="">— {t("admin.doc_edit.none") || "none"} —</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{t(`portal.upload.cat.${c}`) || c.replace(/_/g, " ")}</option>
              ))}
            </select>
            {form.category_key === "other" && (
              <input
                type="text"
                value={form.category_other}
                onChange={(e) => setField("category_other", e.target.value)}
                placeholder={t("portal.upload.category_other_placeholder") || "Describe the category…"}
                className="rn3-input w-full text-sm mt-2"
                data-testid="admin-doc-edit-category-other"
              />
            )}
          </div>

          <div>
            <label className="text-xs font-medium block mb-1">{t("portal.upload.subcategory_label") || "Subcategory"}</label>
            <input
              type="text"
              value={form.subcategory}
              onChange={(e) => setField("subcategory", e.target.value)}
              placeholder={t("portal.upload.subcategory_placeholder") || "e.g. Gas, Oil change, Client meal…"}
              className="rn3-input w-full text-sm"
              data-testid="admin-doc-edit-subcategory"
            />
          </div>

          <div>
            <label className="text-xs font-medium block mb-1">{t("portal.upload.notes_label") || "Notes"}</label>
            <textarea rows={3} value={form.notes} onChange={(e) => setField("notes", e.target.value)} className="rn3-input w-full text-sm" data-testid="admin-doc-edit-notes" />
          </div>
        </div>

        <div className="p-6 border-t border-[#E5E5E5] flex justify-end gap-2">
          <button onClick={onClose} className="rn3-btn-ghost text-xs" data-testid="admin-doc-edit-cancel">{t("admin.doc_edit.cancel") || "Cancel"}</button>
          <button onClick={save} disabled={saving} className="rn3-btn-primary text-xs disabled:opacity-40" data-testid="admin-doc-edit-save">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {t("admin.doc_edit.save") || "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
